/**
 * Shared visual semantics for News Intelligence — sentiment / signal /
 * severity styling lives here so every chip in the tab reads the same.
 *
 * Returns Tailwind class fragments rather than hex codes so that the tab
 * inherits the project's neutral / primary / amber / red / green palette
 * (defined in tailwind.config.js) and stays dark-mode friendly when the
 * surrounding shell adds dark variants.
 */

import type {
  Confidence,
  InvestmentStance,
  Likelihood,
  Sentiment,
  Severity,
  SignalStrength,
  RiskOrigin,
} from './types';

export interface ToneStyle {
  pill: string;          // for full background pills
  border: string;        // for accent borders / gradients
  text: string;          // standalone text
  dot: string;           // small status dots
}

const NEUTRAL: ToneStyle = {
  pill: 'bg-neutral-100 text-neutral-700 border border-neutral-200',
  border: 'border-neutral-300',
  text: 'text-neutral-700',
  dot: 'bg-neutral-400',
};

const GREEN: ToneStyle = {
  pill: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  border: 'border-emerald-300',
  text: 'text-emerald-700',
  dot: 'bg-emerald-500',
};

const RED: ToneStyle = {
  pill: 'bg-red-50 text-red-700 border border-red-200',
  border: 'border-red-300',
  text: 'text-red-700',
  dot: 'bg-red-500',
};

const AMBER: ToneStyle = {
  pill: 'bg-amber-50 text-amber-800 border border-amber-200',
  border: 'border-amber-300',
  text: 'text-amber-800',
  dot: 'bg-amber-500',
};

const BLUE: ToneStyle = {
  pill: 'bg-blue-50 text-blue-700 border border-blue-200',
  border: 'border-blue-300',
  text: 'text-blue-700',
  dot: 'bg-blue-500',
};

const VIOLET: ToneStyle = {
  pill: 'bg-violet-50 text-violet-700 border border-violet-200',
  border: 'border-violet-300',
  text: 'text-violet-700',
  dot: 'bg-violet-500',
};

const ORANGE: ToneStyle = {
  pill: 'bg-orange-50 text-orange-700 border border-orange-200',
  border: 'border-orange-300',
  text: 'text-orange-700',
  dot: 'bg-orange-500',
};

const norm = (v?: string | null) => (v ?? '').toString().trim().toLowerCase();

export function sentimentTone(s?: Sentiment | null): ToneStyle {
  switch (norm(s)) {
    case 'bullish':
    case 'positive':
      return GREEN;
    case 'bearish':
    case 'negative':
      return RED;
    case 'cautious':
    case 'mixed':
      return AMBER;
    case 'neutral':
      return BLUE;
    default:
      return NEUTRAL;
  }
}

export function stanceTone(s?: InvestmentStance | null): ToneStyle {
  return sentimentTone(s);
}

export function signalTone(s?: SignalStrength | null): ToneStyle {
  switch (norm(s)) {
    case 'high':
    case 'strong':
      return VIOLET;
    case 'medium':
    case 'moderate':
      return BLUE;
    case 'low':
    case 'weak':
      return NEUTRAL;
    default:
      return NEUTRAL;
  }
}

const SEVERITY_RANK: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

const LIKELIHOOD_RANK: Record<string, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

export function severityRank(s?: Severity | null): number {
  return SEVERITY_RANK[norm(s)] ?? 0;
}

export function likelihoodRank(s?: Likelihood | null): number {
  return LIKELIHOOD_RANK[norm(s)] ?? 0;
}

export function severityTone(s?: Severity | null): ToneStyle {
  switch (norm(s)) {
    case 'critical':
      return RED;
    case 'high':
      return ORANGE;
    case 'medium':
      return AMBER;
    case 'low':
      return GREEN;
    default:
      return NEUTRAL;
  }
}

export function confidenceTone(c?: Confidence | null): ToneStyle {
  switch (norm(c)) {
    case 'high':
      return GREEN;
    case 'medium':
      return AMBER;
    case 'low':
      return RED;
    default:
      return NEUTRAL;
  }
}

export function originTone(o?: RiskOrigin | null): ToneStyle {
  switch (norm(o)) {
    case 'inferred':
      return VIOLET;
    case 'explicit':
      return BLUE;
    default:
      return NEUTRAL;
  }
}

export function formatLabel(value?: string | null, fallback = '—'): string {
  if (!value) return fallback;
  const cleaned = value.replace(/[_-]+/g, ' ').trim();
  if (!cleaned) return fallback;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
}

export function formatTimestamp(value?: string | number | null): string {
  if (!value) return '—';
  try {
    const d = typeof value === 'number' ? new Date(value) : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(value);
  }
}

export function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const SIGNAL_RANK_MAP: Record<string, number> = {
  strong: 3, high: 3,
  medium: 2, moderate: 2,
  low: 1, weak: 1,
};

export function signalRank(s?: SignalStrength | null): number {
  return SIGNAL_RANK_MAP[norm(s)] ?? 0;
}

/**
 * Composite risk score: severity × likelihood (max = 4×3 = 12).
 * Used to sort and size risks so the most impactful ones dominate visually.
 */
export function riskScore(severity?: Severity, likelihood?: Likelihood): number {
  return severityRank(severity) * likelihoodRank(likelihood);
}

export const MAX_RISK_SCORE = 12;

/** Confidence level → 0–100 percentage for meters / progress bars. */
export function confidencePct(c?: Confidence | null): number {
  switch (norm(c)) {
    case 'high': return 100;
    case 'medium': return 62;
    case 'low': return 28;
    default: return 0;
  }
}
